import React from 'react';
import { NicheTags } from './AccountQualityCheck';

const fieldLabel = {
  margin: '0 0 6px',
  fontSize: 11.5,
  fontWeight: 700,
  color: 'var(--rh-text-faint)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function CloseIcon() {
  return (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function NicheFitSection({ tier, title, items }) {
  return (
    <div className={`rh-fit-section rh-fit-${tier}`}>
      <div className="rh-fit-section-title">
        <span className="rh-fit-dot" />
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <p className="rh-fit-empty">No clients in this tier.</p>
      ) : (
        <div className="rh-fit-list">
          {items.map((it) => {
            const pair = it.matched_account_niche && it.matched_client_niche
              ? `${it.matched_account_niche} ↔ ${it.matched_client_niche}`
              : it.matched_account_niche || it.matched_client_niche || '';
            return (
              <div className="rh-fit-card" key={it.client_name}>
                <div className="rh-fit-card-top">
                  <div className="rh-fit-card-name">{it.client_name}</div>
                  {typeof it.score === 'number' && it.score > 0 && (
                    <div className={`rh-fit-card-score rh-fit-card-score-${tier}`}>
                      {Math.round(it.score * 100)}% emb.
                    </div>
                  )}
                </div>
                {pair && (
                  <div className="rh-fit-card-pair" title={pair}>{pair}</div>
                )}
                {it.reason && (
                  <div className="rh-fit-card-reason">{it.reason}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Inline Client Fit body (used by Account Quality bottom panel and the modal). */
export function NicheFitContent({ account, showNiches = true, emptyHint }) {
  const fit = account?.niche_fit || {};
  const niches = account?.niches || [];
  const bestFit = fit.best_fit || [];
  const averageFit = fit.average_fit || [];
  const poorFit = fit.poor_fit || [];
  const hasFitData = bestFit.length + averageFit.length + poorFit.length > 0;
  const hasNiches = Array.isArray(niches) && niches.length > 0;

  const defaultEmpty = !hasNiches
    ? 'No niches were identified from this account\'s public activity, so client fit could not be scored.'
    : 'Client fit hasn\'t been computed yet. Re-run the quality check, or run "Sync Client Data" if client descriptions were just updated.';

  return (
    <>
      <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '0 0 16px', lineHeight: 1.45 }}>
        Niches and Client Fit help with assignment matching. They do not pass or fail the Account Quality check.
      </p>
      {showNiches && (
        <div style={{ marginBottom: 20 }}>
          <p style={fieldLabel}>Account Niches</p>
          <NicheTags niches={niches} emptyText="No niches identified yet." />
        </div>
      )}

      {!hasFitData ? (
        <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: 0 }}>
          {emptyHint || defaultEmpty}
        </p>
      ) : (
        <>
          <NicheFitSection tier="best" title="Best Fit" items={bestFit} />
          <NicheFitSection tier="average" title="Average Fit" items={averageFit} />
          <NicheFitSection tier="poor" title="Poor Fit" items={poorFit} />
        </>
      )}
    </>
  );
}

export function NicheFitModal({ account, onClose }) {
  if (!account) return null;
  const fit = account.niche_fit || {};

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 560, width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="rh-modal-header">
          <div>
            <h3 className="rh-modal-title">Client Fit — u/{account.username}</h3>
            {fit.computed_at && (
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--rh-text-muted)' }}>
                Computed {new Date(fit.computed_at).toLocaleString()}
              </p>
            )}
          </div>
          <button className="rh-modal-close" onClick={onClose} type="button"><CloseIcon /></button>
        </div>

        <div className="rh-modal-body" style={{ padding: '20px 32px', overflowY: 'auto' }}>
          <NicheFitContent account={account} showNiches />
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn rh-btn-secondary" onClick={onClose} type="button">Close</button>
        </div>
      </div>
    </div>
  );
}

export default NicheFitModal;
